// Header bot picker (hub-ui-step1 spec U5): search-select over the bots the caller can open
// (GET /api/me/bots — own bots; global admins search all with 2+ chars) plus recent bots.
// Choosing a bot keeps the current workspace section.
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useBot } from '../contexts/BotContext';
import { hubClient, type MyBots } from '../services/hubClient';
import { botLabel, botPath, freshFor, moveActive, parseRecent, pushRecent, type RecentBot } from '../lib/botNav';
import { pickerAutoOpen, pickerSection, systemTag } from '../lib/systemScope';

const RECENT_KEY = 'hub-ui.recentBots';

function loadRecent(): RecentBot[] {
  try {
    return parseRecent(localStorage.getItem(RECENT_KEY));
  } catch {
    return [];
  }
}

function saveRecent(list: RecentBot[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* private mode / quota: recent list is a convenience */
  }
}

type Option = RecentBot & { hint: string | null; group: string };

export function BotPicker() {
  const { system, botId, setScope } = useBot();
  const navigate = useNavigate();
  const location = useLocation();
  // Opens by itself on the choose-a-bot landing (fresh browser, no bot chosen).
  const [open, setOpen] = useState(() => pickerAutoOpen({ system, botId }, location.pathname));
  const [q, setQ] = useState('');
  // Each answer remembers the query it answered: a list for an older query is never shown or chosen.
  const [result, setResult] = useState<(MyBots & { q: string }) | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(-1);
  const [recent, setRecent] = useState<RecentBot[]>(loadRecent);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced lookup while open; a response for an older query is dropped.
  useEffect(() => {
    if (!open) return;
    let stale = false;
    setLoading(true);
    const term = q.trim();
    const t = setTimeout(() => {
      hubClient
        .myBots(term)
        .then((r) => {
          if (stale) return;
          setResult({ ...r, q: term });
          setError(false);
        })
        .catch(() => {
          if (!stale) setError(true);
        })
        .finally(() => {
          if (!stale) setLoading(false);
        });
    }, 250);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [open, q]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const fresh = freshFor(result, q);
  const options: Option[] = useMemo(() => {
    const searching = q.trim().length > 0;
    const group = fresh?.scope === 'all' ? 'All bots' : searching ? 'Matches' : 'Your bots';
    const fromApi: Option[] = (fresh?.bots ?? []).map((b) => ({
      system: b.system,
      botId: b.botId,
      name: b.name,
      hint: b.tenantName ?? b.role,
      group,
    }));
    if (searching) return fromApi;
    const seen = new Set(recent.map((r) => `${r.system}:${r.botId}`));
    return [...recent.map((r) => ({ ...r, hint: null, group: 'Recent' })), ...fromApi.filter((o) => !seen.has(`${o.system}:${o.botId}`))];
  }, [fresh, recent, q]);

  useEffect(() => setActive(options.length ? 0 : -1), [options]);

  function choose(o: RecentBot) {
    const ref = { system: o.system, botId: o.botId };
    const next = pushRecent(recent, { ...ref, name: o.name });
    setRecent(next);
    saveRecent(next);
    setScope(ref);
    navigate(botPath(ref, pickerSection(location.pathname)));
    setOpen(false);
    setQ('');
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => moveActive(i, 1, options.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => moveActive(i, -1, options.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = options[active];
      if (o) choose(o);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const current = recent.find((r) => r.system === system && r.botId === botId);
  const label = current ? botLabel(current) : botId || 'Choose a bot';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
      >
        {systemTag(system) && <span className="rounded bg-gray-100 px-1.5 text-[11px] text-gray-500">{systemTag(system)}</span>}
        <span className="max-w-56 truncate font-medium">{label}</span>
        <span aria-hidden className="text-gray-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-96 rounded border border-gray-200 bg-white shadow-lg">
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search bots by name, id or bot_no"
            aria-label="Search bots"
            role="combobox"
            aria-expanded="true"
            aria-controls="bot-picker-list"
            aria-activedescendant={active >= 0 ? `bot-opt-${active}` : undefined}
            className="w-full border-b border-gray-200 px-3 py-2 text-sm outline-none"
          />
          <ul id="bot-picker-list" role="listbox" className="max-h-80 overflow-y-auto py-1 text-sm">
            {options.map((o, i) => (
              <Fragment key={`${o.system}:${o.botId}`}>
                {(i === 0 || options[i - 1].group !== o.group) && (
                  <li role="presentation" className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {o.group}
                  </li>
                )}
                <li
                  id={`bot-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(o);
                  }}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${i === active ? 'bg-blue-50' : ''}`}
                >
                  <span className="rounded bg-gray-100 px-1.5 text-[11px] text-gray-500">{systemTag(o.system)}</span>
                  <span className="truncate">{botLabel(o)}</span>
                  <span className="ml-auto truncate text-xs text-gray-400">{o.hint ?? o.botId}</span>
                </li>
              </Fragment>
            ))}
            {loading && <li className="px-3 py-2 text-gray-400">Loading…</li>}
            {!loading && error && <li className="px-3 py-2 text-red-600">Bot list unavailable. Recent bots still work.</li>}
            {!loading && !error && !q.trim() && (fresh?.unsynced ?? 0) > 0 && (
              <li className="px-3 py-2 text-xs text-gray-400">{fresh!.unsynced} of your bots are not synced to hub yet.</li>
            )}
            {!loading && !error && options.length === 0 && (
              <li className="px-3 py-2 text-gray-500">{q.trim() ? 'No bots match.' : 'You are not a member of any bot yet.'}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
